import {
  FunctionComponent,
  useEffect,
  useState,
  useContext,
  useCallback,
  ReactNode,
} from 'react'
import styled, { css, useTheme } from 'styled-components'

import contextVideo, { ManagedVideo } from '@contexts/video'
import contextBlickTV from '@contexts/blickTV'
import { isMobileUserAgent } from '@measures/responsive'
import { toggleThemeColor } from './utils'

const { VideoContext } = contextVideo
const { BlickTVContext } = contextBlickTV

// Function to check the instance id of the video (in rare case multiple players showing the same video are in the same page)
const getInstanceId = (id?: string) =>
  id ? id.substring('vjs_video_'.length, id.length) : ''

let currentVideo: ManagedVideo | undefined

const isSelfHostedVideo = (videoType: ManagedVideo['type']) =>
  ['blicktv', 'integrated'].includes(videoType)

const DarkOverlayBackground = styled.div<{ isOverlay: boolean }>`
  ${({
    theme: {
      colors: {
        blicktvGrey: { btvGrey_100 },
      },
    },
  }) => css`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: ${btvGrey_100};
  `};
  ${({ isOverlay }) => `display: ${isOverlay ? 'block' : 'none'};`}
`

const VideoContextProvider: FunctionComponent<{ children?: ReactNode }> = ({
  children,
}) => {
  const [isOverlay, setIsOverlay] = useState<boolean>(false)
  const { unmount, setBlickTVCleanupFunction } = useContext(BlickTVContext)
  const theme = useTheme()

  const showOverlay = useCallback(() => {
    //! we need `toggleThemeColor` because of Safari - Apple moved the address bar to the bottom and introduced something called a theme-color
    //! https://css-tricks.com/safari-15-new-ui-theme-colors-and-a-css-tricks-cameo/
    //! So whenever a user is in fullscreen mode it should turn to black, because otherwise some of the page is staying white
    toggleThemeColor(theme)

    if (isMobileUserAgent()) {
      //! Set background to black, so that when we switch to
      //! landscape we don't have white background in the
      //! safe area of an iPhone X / Xs / 11..
      document?.body?.classList?.add?.('video-overlay-shown')

      //! Scroll down to hide the tab bar on landscape mode
      window?.scrollTo?.(0, (window?.scrollY ?? 0) + 100)
    }

    setIsOverlay(true)
  }, [theme])

  const hideOverlay = useCallback(() => {
    //! we need `toggleThemeColor` because of Safari - Apple moved the address bar to the bottom and introduced something called a theme-color
    //! https://css-tricks.com/safari-15-new-ui-theme-colors-and-a-css-tricks-cameo/
    //! So whenever a user is in fullscreen mode it should turn to black, because otherwise some of the page is staying white
    toggleThemeColor(theme)

    if (isMobileUserAgent()) {
      document?.body?.classList?.remove?.('video-overlay-shown')

      //! Scroll down to hide the tab bar on landscape mode
      window?.scrollTo?.(0, (window?.scrollY ?? 0) - 100)
    }

    setIsOverlay(false)
  }, [theme])

  const addVideo = useCallback(
    (video: ManagedVideo) => {
      if (
        currentVideo &&
        (currentVideo.id !== video.id ||
          (isSelfHostedVideo(video.type) &&
            getInstanceId(currentVideo.ref?.current?.player?.()?.id_) !==
              getInstanceId(video.ref?.current?.player?.()?.id_)))
      ) {
        if (isSelfHostedVideo(currentVideo.type)) {
          if (currentVideo.type === 'blicktv') {
            unmount()
          } else {
            try {
              currentVideo.ref?.current?.player?.()?.ima3?.adPlayer?.pause?.()
            } catch (e) {
              // empty
            }

            currentVideo.ref?.current?.pause?.()
          }
        } else if (currentVideo.type === 'youtube') {
          currentVideo.ref?.pauseVideo?.()
        }
      }

      currentVideo = { ...video }
    },
    [unmount]
  )

  const pauseVideo = useCallback(() => {
    currentVideo?.ref.current?.pause?.()
    currentVideo = undefined
  }, [])

  useEffect(() => {
    setBlickTVCleanupFunction(() => {
      if (currentVideo?.type === 'blicktv') {
        currentVideo = undefined
      }
    })
    return () => {
      setBlickTVCleanupFunction(() => {})
    }
  }, [setBlickTVCleanupFunction, pauseVideo])

  return (
    <>
      <VideoContext.Provider
        value={{
          isOverlay,
          addVideo,
          pauseVideo,
          showOverlay,
          hideOverlay,
        }}>
        {children}
      </VideoContext.Provider>
      <DarkOverlayBackground isOverlay={isOverlay} />
    </>
  )
}

export default VideoContextProvider
